<template>
	<!--
		Intro view intentionally maps the slots so Close is the primary (agree) button and See More is the
		secondary (cancel) button: most users just want to dismiss the dialog, only some want the detail.
		See More (cancel slot) advances to detail; Close (agree slot) dismisses. Keep this mapping when editing
		the cancel-text/agree-text props below — see onAgree/onCancel for the matching handler logic.
	-->
	<Dialog
		:dialog-visible="showDialog"
		title="Data Privacy"
		:options="dialogOptions"
		:cancel-text="view === 'detail' ? 'Back' : 'See More'"
		:agree-text="view === 'detail' ? 'Download' : 'Close'"
		:custom-agree-icon="view === 'detail' ? 'mdi-download' : ''"
		:custom-outside-click-function="close"
		@agree="onAgree"
		@cancel="onCancel"
		@after-leave="resetView">
		<template #dialog-body>
			<p v-if="view === 'intro'" class="text-body-2 mt-2">
				At LiveSwitch, the content and media you create&mdash;photos, videos, documents, and more&mdash;remain yours. We do not sell
				your data. We only share it with trusted service providers (like AWS) who help us operate the platform or when required by
				law. You can use LiveSwitch knowing that we are serious about keeping your data secure, private, and under your control.
			</p>
			<img v-else src="/LiveSwitch_Data_Privacy.png" alt="LiveSwitch Data Privacy & Protection infographic" class="privacy-image" />
		</template>
	</Dialog>
</template>

<script lang="ts" setup>
	import { computed, ref } from "vue";
	import Dialog from "./Dialog.vue";
	import { downloadDataPrivacyInfographic } from "@/helpers/downloadDataPrivacyInfographic";

	type View = "intro" | "detail";

	const showDialog = ref(false);
	const view = ref<View>("intro");

	const dialogOptions = computed(() => (view.value === "detail" ? { width: "880px", fullHeight: true } : { width: "560px" }));

	defineExpose({ open });

	function open() {
		view.value = "intro";
		showDialog.value = true;
	}

	function onAgree() {
		// Intro's primary (agree) button is Close; detail's is Download.
		if (view.value === "intro") {
			close();
		} else {
			downloadDataPrivacyInfographic();
		}
	}

	function onCancel() {
		// Intro's secondary (cancel) button is See More, which advances to detail;
		// detail's is Back, which returns to intro.
		// Esc / click-outside always close (routed via customOutsideClickFunction).
		if (view.value === "detail") {
			view.value = "intro";
		} else {
			view.value = "detail";
		}
	}

	function close() {
		showDialog.value = false;
	}

	function resetView() {
		view.value = "intro";
	}
</script>

<style lang="scss" scoped>
	.privacy-image {
		display: block;
		width: 100%;
		height: auto;
	}
</style>
